import * as XLSX from "xlsx";

const MAX_SHIPMENT_ROWS = 500;

/**
 * Reads a shipment import sheet into the rows ShipmentImport expects, with the shipment and order
 * number generated from marketplace + PO (same rule as the Shipments page import).
 *
 * `getTracking` is part of the duplicate key: with labels the customer bought, the same SKU twice on
 * a PO is two boxes with two labels, not a duplicate row.
 *
 * Rows missing PO Number, Customer Name or SKU are not imported; they are returned in `skipped` (with
 * their Excel row number and what is missing) so the user is told instead of losing them silently.
 *
 * Resolves { items, duplicates, skipped } or { error }.
 */
const REQUIRED_COLUMNS = ["PO Number", "Customer Name", "SKU"];

export const readShipmentSheet = (file, getTracking = () => "") =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onerror = () =>
      resolve({ error: "The Excel file could not be read." });
    reader.onload = (e) => {
      let allRows;
      try {
        const workbook = XLSX.read(e.target.result, {
          type: "binary",
          cellDates: true,
          dateNF: "yyyy-mm-dd",
        });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        allRows = XLSX.utils.sheet_to_json(sheet, { raw: false });
      } catch (error) {
        resolve({ error: "The Excel file could not be read." });
        return;
      }
      const rows = [];
      const skipped = [];
      allRows.forEach((row, index) => {
        const missing = REQUIRED_COLUMNS.filter((column) => !row[column]);
        if (!missing.length) {
          rows.push(row);
        } else {
          skipped.push({
            row: (row.__rowNum__ ?? index + 1) + 1,
            missing,
          });
        }
      });

      if (!rows.length) {
        resolve({
          error:
            "Please add PO Number, Customer Name, SKU columns in your excel file",
        });
        return;
      }
      if (rows.some((row) => !String(row["Marketplace"] || "").trim())) {
        resolve({ error: "Every row needs a Marketplace." });
        return;
      }
      if (rows.length > MAX_SHIPMENT_ROWS) {
        resolve({ error: "Shipment count cannot be more than 500." });
        return;
      }

      const seen = new Set();
      const items = [];
      const duplicates = [];
      rows.forEach((row) => {
        const key = `${row["PO Number"]}__${row["SKU"]}__${getTracking(row)}`;
        if (seen.has(key)) {
          duplicates.push(row);
        } else {
          seen.add(key);
          items.push(row);
        }
      });

      items.forEach((row, index) => {
        const marketplace = String(row["Marketplace"] || "");
        const shipmentNumber =
          "obt-" +
          marketplace[0].toLowerCase() +
          marketplace
            .slice(1)
            .toLowerCase()
            .replace(/[aeiou&%/?+#\s]/g, "")
            .substring(0, 2) +
          "-" +
          row["PO Number"];
        row["Shipment Number"] = shipmentNumber;
        row["Order Number"] = shipmentNumber;
        row["Line Number"] = index + 2;
      });

      resolve({ items, duplicates, skipped });
    };
    reader.readAsBinaryString(file);
  });
